import React from 'react';
import { CheckCircle2, Clock, AlertTriangle, ChevronRight, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PipelineStageKey =
  | 'REQUEST'
  | 'APPROVAL'
  | 'SAP_PR'
  | 'RFQ'
  | 'QUOTATION'
  | 'SUPPLIER'
  | 'PO'
  | 'DELIVERY'
  | 'GRN'
  | 'INVOICE'
  | 'PAYMENT'
  | 'CLOSED';

export interface PipelineStage {
  key: PipelineStageKey;
  label: string;
  status: 'completed' | 'active' | 'pending' | 'blocked' | 'error';
  documentNumber?: string;
}

interface ProcurementPipelineProps {
  currentStage: PipelineStageKey;
  stages?: PipelineStage[];
  onStageClick?: (stageKey: PipelineStageKey) => void;
  className?: string;
}

export const DEFAULT_LIFECYCLE_STAGES: PipelineStage[] = [
  { key: 'REQUEST', label: 'Requisition', status: 'completed', documentNumber: 'REQ-1001' },
  { key: 'APPROVAL', label: 'NFA Approval', status: 'completed', documentNumber: 'NFA-1001' },
  { key: 'SAP_PR', label: 'SAP PR Sync', status: 'completed', documentNumber: 'PR-450001' },
  { key: 'RFQ', label: 'RFQ Sourcing', status: 'completed', documentNumber: 'RFQ-2001' },
  { key: 'QUOTATION', label: 'Quotations', status: 'completed', documentNumber: 'QUO-3001' },
  { key: 'SUPPLIER', label: 'LFA Award', status: 'completed', documentNumber: 'LFA-1001' },
  { key: 'PO', label: 'Purchase Order', status: 'completed', documentNumber: 'PO-5001' },
  { key: 'DELIVERY', label: 'Delivery', status: 'completed' },
  { key: 'GRN', label: 'Goods Receipt', status: 'completed', documentNumber: 'GRN-6001' },
  { key: 'INVOICE', label: '3-Way Match', status: 'completed', documentNumber: 'INV-7001' },
  { key: 'PAYMENT', label: 'Payment', status: 'completed', documentNumber: 'PAY-8001' },
  { key: 'CLOSED', label: 'Archived', status: 'completed' },
];

export const ProcurementPipeline: React.FC<ProcurementPipelineProps> = ({
  currentStage,
  stages = DEFAULT_LIFECYCLE_STAGES,
  onStageClick,
  className,
}) => {
  return (
    <div className={cn('w-full rounded-lg border border-border bg-card p-4 shadow-xs custom-scrollbar overflow-x-auto', className)}>
      <div className="flex items-center justify-between gap-1 min-w-[960px]">
        {stages.map((stage, idx) => {
          const isCurrent = stage.key === currentStage;
          let stageColor = 'bg-stone-100 text-stone-500 border-stone-200 dark:bg-stone-900 dark:text-stone-400';
          let Icon = Lock;

          if (stage.status === 'completed') {
            stageColor = 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-500/30';
            Icon = CheckCircle2;
          } else if (stage.status === 'active' || isCurrent) {
            stageColor = 'bg-primary/10 text-primary border-primary/40 font-bold ring-2 ring-primary/20';
            Icon = Clock;
          } else if (stage.status === 'error') {
            stageColor = 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30';
            Icon = AlertTriangle;
          } else if (stage.status === 'blocked') {
            stageColor = 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
            Icon = AlertTriangle;
          }

          return (
            <React.Fragment key={stage.key}>
              <div
                onClick={() => onStageClick && onStageClick(stage.key)}
                className={cn(
                  'flex flex-1 flex-col items-center justify-center gap-1 rounded-md border p-2 text-center transition-all select-none',
                  stageColor,
                  onStageClick && 'cursor-pointer hover:border-primary/50'
                )}
              >
                <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider">
                  <Icon className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{stage.label}</span>
                </div>
                {stage.documentNumber ? (
                  <span className="font-mono text-[10px] opacity-80 truncate">{stage.documentNumber}</span>
                ) : (
                  <span className="text-[10px] opacity-40">—</span>
                )}
              </div>
              {idx < stages.length - 1 && (
                <ChevronRight className="h-4 w-4 text-border-strong flex-shrink-0 opacity-60" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
